import { Injectable, inject, signal } from '@angular/core';
import { of } from 'rxjs';
import { catchError, finalize, throttleTime } from 'rxjs/operators';
import { IUserNotification, NotificationsHubService, UserNotificationService } from '@nha/api/notifications';
import { BusinessSystem } from '@nha/enums';
import { INotification } from '../interfaces';
import { buildAppDisplay } from '../functions/app-display.function';

/** Rows rendered in the panel before "Load more" is pressed. */
const PAGE_SIZE = 5;

/**
 * How many notifications are kept per request. Deliberately close to PAGE_SIZE so the panel
 * only holds a recent window rather than a user's whole history — "Load more" pages the rest.
 *
 * The badge counts unread rows within that window, so it is a floor rather than a true total
 * whenever the server has more; unreadCountIsPartial says so and the bell renders a "+". The
 * API has no unread-count endpoint to ask instead.
 */
const FETCH_SIZE = 20;

/**
 * Every request asks for one row past what it keeps. Without that probe a page that exactly
 * fills the window is indistinguishable from the end of the list, so the panel offers a "Load
 * more" that fetches nothing and the badge wears a "+" it hasn't earned.
 */
const PROBE_ROW = 1;

/**
 * Opening the panel within this window of the last successful load reuses what's already in
 * state. Without it every bell click refetches data the mount load just fetched.
 */
const REFRESH_AFTER_MS = 60000;

/**
 * A burst of hub pushes (e.g. a bulk job creating notifications) collapses into at most one
 * reload per window — leading so the first push lands instantly, trailing so the last one
 * isn't dropped.
 */
const REALTIME_THROTTLE_MS = 1000;

@Injectable({ providedIn: 'root' })
export class NotificationMenuStore {
	private _service = inject(UserNotificationService);
	private _hub = inject(NotificationsHubService);
	private _listeningForRealtime = false;

	private _businessSystemID: number = BusinessSystem.MyNHA;
	/** Every notification fetched so far; the panel renders a growing slice of this. */
	private _fetched: IUserNotification[] = [];
	private _visibleCount = 0;
	private _serverExhausted = false;
	/** Timestamp of the last successful load; 0 until one succeeds, so failures always retry. */
	private _lastLoadedAt = 0;

	/**
	 * Bumped by every request so a response that was superseded while in flight is dropped
	 * instead of writing rows into a window the user has since paged or reloaded past.
	 */
	private _requestID = 0;

	/**
	 * Rows marked read here rather than by the server. A fetch already in flight when the row
	 * was clicked predates the PUT, so its response still says unread — these IDs are re-applied
	 * over any response to stop the row flipping back under the user.
	 */
	private _locallyRead = new Set<number>();

	notifications = signal<INotification[]>([]);
	isLoading = signal<boolean>(false);
	isLoadingMore = signal<boolean>(false);
	hasError = signal<boolean>(false);
	unreadCount = signal<number>(0);
	/** True while unfetched rows remain on the server, making unreadCount a floor rather than a total. */
	unreadCountIsPartial = signal<boolean>(false);
	hasMore = signal<boolean>(false);

	/**
	 * Bumped once per realtime push that actually brought a row the panel hadn't seen. A push is
	 * only a hint that something was created — the reload it triggers is entitlement-filtered, so
	 * a notification the user isn't allowed to see arrives as a push with nothing behind it. Ringing
	 * off the push itself would chime at a list that never changed.
	 */
	realtimePulse = signal<number>(0);

	/** Called once when the bell mounts so the badge is populated before the panel is opened. */
	initialize(businessSystemID: number): void {
		this._businessSystemID = businessSystemID;
		this.load();
		this.listenForRealtime();
	}

	/**
	 * The list and badge are filtered server-side by the user's in-app settings, so switching a
	 * type off drops rows that are already in the panel. Refetch when the settings dialog saves,
	 * rather than leaving the bell showing notifications the user just muted.
	 */
	settingsChanged(): void {
		this.load(false);
	}

	/** Refreshes in place when the panel opens, without blanking a list that already has rows. */
	refresh(): void {
		if (this.isLoading() || this.isLoadingMore()) {
			return;
		}

		if (this._lastLoadedAt && Date.now() - this._lastLoadedAt < REFRESH_AFTER_MS) {
			return;
		}

		this.load(this.notifications().length === 0);
	}

	load(showSkeleton: boolean = true, ringOnNewRows: boolean = false): void {
		/**
		 * In-place reloads (panel refresh, realtime push) keep the rows "Load more" already
		 * revealed instead of collapsing the list back to the first page under the user.
		 */
		const keepVisible = showSkeleton ? 0 : this._visibleCount;

		/**
		 * An in-place reload has to cover everything "Load more" already revealed, or a push that
		 * lands after paging would refetch a single window and collapse the list under the user.
		 */
		const take = Math.max(FETCH_SIZE, keepVisible);

		/** Nobody asked for an in-place reload, so it fails quietly rather than interrupting. */
		const isBackground = !showSkeleton;
		const requestID = ++this._requestID;

		if (showSkeleton) {
			this.isLoading.set(true);
			this.hasError.set(false);
			this.notifications.set([]);
		}

		/**
		 * Paging state is deliberately left standing until the response lands. Tearing it down up
		 * front leaves rows on screen that no longer have state behind them, so a click during the
		 * request would blank the list and lose the read it just applied.
		 */
		this._service
			.getNotifications(this._businessSystemID, 0, take + PROBE_ROW, isBackground)
			.pipe(
				catchError(() => {
					if (!isBackground && requestID === this._requestID) {
						this.hasError.set(true);
					}

					return of(null as IUserNotification[]);
				}),
				finalize(() => this.settle(requestID))
			)
			.subscribe((notifications) => {
				if (notifications === null || requestID !== this._requestID) {
					return;
				}

				const knownRecipientIDs = new Set(this._fetched.map((n) => n.notificationRecipientID));

				this._serverExhausted = notifications.length <= take;
				this._fetched = notifications.slice(0, take).map((n) => this.applyLocalRead(n));
				this._visibleCount = Math.min(Math.max(PAGE_SIZE, keepVisible), this._fetched.length);
				this._lastLoadedAt = Date.now();
				this.hasError.set(false);

				this.publish();
				this.publishUnreadCount();

				if (ringOnNewRows && this._fetched.some((n) => !knownRecipientIDs.has(n.notificationRecipientID))) {
					this.realtimePulse.update((pulse) => pulse + 1);
				}
			});
	}

	loadMore(): void {
		// everything already fetched is shown before going back to the server
		if (this._visibleCount < this._fetched.length) {
			this._visibleCount = Math.min(this._visibleCount + PAGE_SIZE, this._fetched.length);
			this.publish();
			return;
		}

		if (this._serverExhausted) {
			return;
		}

		const skip = this._fetched.length;
		const requestID = ++this._requestID;

		this.isLoadingMore.set(true);

		this._service
			.getNotifications(this._businessSystemID, skip, FETCH_SIZE + PROBE_ROW)
			.pipe(
				catchError(() => of(null as IUserNotification[])),
				finalize(() => this.settle(requestID))
			)
			.subscribe((notifications) => {
				if (notifications === null || requestID !== this._requestID) {
					return;
				}

				this._serverExhausted = notifications.length <= FETCH_SIZE;
				this._fetched = [...this._fetched, ...notifications.slice(0, FETCH_SIZE).map((n) => this.applyLocalRead(n))];
				this._visibleCount = Math.min(this._visibleCount + PAGE_SIZE, this._fetched.length);

				this.publish();
				this.publishUnreadCount();
			});
	}

	markRead(notification: INotification): void {
		if (notification.isRead) {
			return;
		}

		// applied straight away so the row and badge respond to the click; rolled back if the call fails
		this.applyRead((n) => n.notificationRecipientID === notification.notificationRecipientID);

		this._service.markAsRead(notification.notificationRecipientID).subscribe({
			error: () => this.resyncReads()
		});
	}

	/** The response body is empty — the observable effect (ring + reload) arrives back over the hub. */
	sendTestNotification(): void {
		this._service.sendTestNotification().subscribe();
	}

	markAllRead(): void {
		this.applyRead(() => true);

		this._service.markAllAsRead(this._businessSystemID).subscribe({
			error: () => this.resyncReads()
		});
	}

	/**
	 * The push only carries a count, and the badge/list are derived from fetched rows — so each
	 * push triggers an in-place reload rather than trusting an incremental count. The ring is left
	 * to that reload: only rows it actually brings back are ones the user may see.
	 */
	private listenForRealtime(): void {
		if (this._listeningForRealtime) {
			return;
		}
		this._listeningForRealtime = true;

		this._hub.newNotifications$
			.pipe(throttleTime(REALTIME_THROTTLE_MS, undefined, { leading: true, trailing: true }))
			.subscribe(() => this.load(false, true));

		// pushes sent while the transport was down are lost — re-sync quietly, without ringing the bell
		this._hub.reconnected$.subscribe(() => this.load(false));
	}

	/**
	 * Only the newest request owns the spinners: an older one settling first would clear the
	 * skeleton out from under the request that replaced it.
	 */
	private settle(requestID: number): void {
		if (requestID !== this._requestID) {
			return;
		}

		this.isLoading.set(false);
		this.isLoadingMore.set(false);
	}

	private applyRead(matches: (notification: IUserNotification) => boolean): void {
		this._fetched = this._fetched.map((n) => {
			if (!matches(n) || n.isSeen) {
				return n;
			}

			this._locallyRead.add(n.notificationRecipientID);
			return { ...n, isSeen: true };
		});

		this.publish();
		this.publishUnreadCount();
	}

	/** The server never took the read, so drop the local one and let the next fetch be the truth. */
	private resyncReads(): void {
		this._locallyRead.clear();
		this.load(false);
	}

	private applyLocalRead(notification: IUserNotification): IUserNotification {
		return this._locallyRead.has(notification.notificationRecipientID) ? { ...notification, isSeen: true } : notification;
	}

	private publish(): void {
		this.notifications.set(this._fetched.slice(0, this._visibleCount).map((n) => this.toView(n)));
		this.hasMore.set(this._visibleCount < this._fetched.length || !this._serverExhausted);
	}

	private publishUnreadCount(): void {
		this.unreadCount.set(this._fetched.filter((n) => !n.isSeen).length);
		this.unreadCountIsPartial.set(!this._serverExhausted);
	}

	private toView(notification: IUserNotification): INotification {
		return {
			notificationID: notification.notificationID,
			notificationRecipientID: notification.notificationRecipientID,
			app: buildAppDisplay(notification),
			subject: notification.subject,
			message: notification.message,
			createdDate: new Date(notification.notificationDate),
			isRead: notification.isSeen,
			deepLinkUrl: notification.url
		};
	}
}
